import uniqBy from "lodash-es/uniqBy";
// Material-UI
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { GridColDef, GridColumnHeaderParams } from "@mui/x-data-grid-premium";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";

// Types
import {
	IInventoryTradeVehicles,
	IVehicle,
	TDealerInventoryTrade,
	TSearchTransferRequest,
	TTransferType,
} from "@/DataPages/components/Inventory/types/dealerTrades";

// Constants
import { TRANSFER_ASSET_SWAP } from "../constants";
import { DATE_FORMAT } from "@/Constants/date";
import { CELL_CLASS_NAME, CUSTOM_PROPS } from "../Tables/commonProps";

// Helpers
import { and } from "@/Common/helpers/sonar";
import { getFormatDate } from "@/Common/helpers/Dates";
import { defaultCellRenderer } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import TransferRequestAction from "./DealerTrades/TransferRequestAction";
import { resolveFiltersDefault } from "../helpers";
import { useMutation } from "@tanstack/react-query";
import { editTransferRequest } from "@/DataPages/api/inventory/mutations";

// Local Types
type EditPendingRequestDialogProps = {
	isOpen: boolean;
	mappedLocations?: Record<string, string>;
	itemSelected?: TDealerInventoryTrade;
	itemToSwap?: TDealerInventoryTrade;
	locationId: string;
	commissionCycleId?: string;
	payPeriodId: string;
	onClose: () => void;
	onSetRowsSelected: () => void;
	isLoadingLocations: boolean;
	franchisesByLocations: {
		franchises: string[];
		locationId: string;
	}[];
	isCurrentPayPeriod: boolean;
};

const renderHeaderHandler = (params: GridColumnHeaderParams) => <TypographyHeader fontSize="12px" fontLineHeight="14px" params={params} />;

const sameProperties = {
	renderHeader: renderHeaderHandler,
	cellClassName: () => CELL_CLASS_NAME,
	renderCell: defaultCellRenderer,
};

const EditPendingRequestDialog = (props: EditPendingRequestDialogProps) => {
	const {
		isOpen,
		mappedLocations = {},
		itemSelected,
		itemToSwap,
		onClose,
		isLoadingLocations,
		franchisesByLocations,
		onSetRowsSelected,
		locationId,
		commissionCycleId,
		payPeriodId,
		isCurrentPayPeriod,
	} = props;
	const currentTrade = uniqBy(
		[itemSelected, itemToSwap].filter((item) => item?._id),
		"_id"
	) as TDealerInventoryTrade[];

	const mappedLocationsArray =
		Object.entries(mappedLocations).map(([value, label]) => {
			return { label, value };
		}) ?? [];

	const columns: GridColDef[] = [
		{
			field: "_id",
			headerName: "ID",
			minWidth: 10,
			width: 10,
			filterable: false,
			resizable: false,
		},
		{
			field: "originLocationId",
			minWidth: 160,
			headerName: "Origin Store",
			...sameProperties,
			valueGetter: (value: string) => mappedLocations?.[value] ?? "",
			renderCell: defaultCellRenderer,
		},
		{
			field: "destinationLocationId",
			minWidth: 160,
			headerName: "Destination Store",
			...sameProperties,
			valueGetter: (value: string) => mappedLocations?.[value] ?? "",
		},
		{
			field: "type",
			width: 50,
			headerName: "Type",
			valueGetter: (_, row) => row?.vehicle?.type ?? "",
			...sameProperties,
		},
		{
			field: "stockNumber",
			headerName: "Stock #",
			width: 80,
			valueGetter: (_, row) => row?.vehicle?.stockNumber ?? "",
			...sameProperties,
		},
		{
			field: "vinNumber",
			width: 120,
			headerName: "VIN",
			valueGetter: (_, row) => row?.vehicle?.vinNumber ?? "",
			...sameProperties,
		},
		{
			field: "make",
			width: 110,
			headerName: "Make",
			valueGetter: (_, row) => row?.vehicle?.make ?? "",
			...sameProperties,
		},
		{
			field: "model",
			width: 110,
			headerName: "Model",
			valueGetter: (_, row) => row?.vehicle?.model ?? "",
			...sameProperties,
		},
		{
			field: "trim",
			width: 110,
			headerName: "Trim",
			valueGetter: (_, row) => row?.vehicle?.trim ?? "",
			...sameProperties,
		},
		{
			field: "colorExterior",
			width: 110,
			headerName: "Ext. Color",
			valueGetter: (_, row) => row?.vehicle?.colorExterior ?? "",
			...sameProperties,
		},
		{
			field: "year",
			width: 50,
			headerName: "Year",
			valueGetter: (_, row) => row?.vehicle?.year ?? "",
			...sameProperties,
		},
		{
			field: "requestedByLocationId",
			minWidth: 185,
			headerName: "Requested By",
			...sameProperties,
			valueGetter: (value: string, params: TDealerInventoryTrade) => {
				if (and(params?.transferType === TRANSFER_ASSET_SWAP, params?.requestedByLocationId === params?.originLocationId)) {
					return `${mappedLocations?.[value] ?? ""}`;
				}

				return `${mappedLocations?.[value] ?? ""} (${params?.requestStatus?.requestedBy})`;
			},
		},
		{
			field: "requestedDate",
			width: 90,
			headerName: "Requested Date",
			...sameProperties,
			valueGetter: (_, row: TDealerInventoryTrade) => getFormatDate(row.requestStatus.requestedDate, DATE_FORMAT.usDateSlash),
		},
	];

	const actionType =
		itemSelected?.destinationLocationId === locationId
			? { transferType: "Transfer In", title: "Receiving" }
			: { transferType: "Transfer Out", title: "Giving" };

	const defaultSearchTransferRequest = resolveFiltersDefault(itemSelected);

	const { mutate } = useMutation({
		mutationKey: ["editTransferRequest", locationId],
		mutationFn: async ({ _id, requestId, swapId, input }: { _id: string; requestId: string; swapId: string; input: IVehicle }) => {
			return editTransferRequest({ input, _id, requestId, swapId, locationId });
		},
	});

	const handleOnEdit = (_filters: Partial<TSearchTransferRequest>, data?: IInventoryTradeVehicles, cleaner?: () => void) => {
		if (data) {
			const rowId = itemSelected?._id ?? "";
			const requestId = itemSelected?.requestId ?? "";
			const swapId = itemSelected?.swapId ?? "";

			const { stock, vin, make, model, trim, colorExterior, year, type, _id, transferAmount, status } = data;

			const input: IVehicle = {
				stockNumber: stock,
				vinNumber: vin,
				year: Number(year),
				_id,
				make,
				model,
				trim,
				colorExterior,
				type,
				transferAmount,
				status,
			};

			mutate(
				{ _id: rowId, requestId, swapId, input },
				{
					onSuccess: () => {
						onSetRowsSelected();
						cleaner?.();
						onClose();
					},
				}
			);
		}
	};

	return (
		<Dialog
			classes={{
				paper: "!rounded-[10px] !shadow-md",
			}}
			open={isOpen}
			onClose={onClose}
			PaperProps={{
				style: {
					maxWidth: "1400px",
					width: "1400px",
				},
			}}
			disableEscapeKeyDown
		>
			<DialogTitle className="!p-8">
				<Typography className={"!font-montserrat !text-[24px] !text-default !font-medium"}>Asset Swap Trade</Typography>
				<IconButton className="!absolute !right-1 !top-1 !text-default" aria-label="close" onClick={onClose}>
					<CloseRoundedIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent className="!px-8 !pb-10">
				<Typography className={"!font-montserrat !text-[20px] !text-default !font-medium pb-8"}>Current Trade Preview</Typography>
				<DataSmartTable
					columns={columns}
					rows={currentTrade}
					maxRows={10}
					enableCustomScrollbars
					disableColumnMenu
					hideFooter
					customProps={CUSTOM_PROPS}
					rowHeight={25}
					autoHeight={false}
					columnVisibilityModel={{ _id: false }}
					isRowSelectable={() => false}
					disableHScrollCorrection={true}
					slots={{
						noRowsOverlay: NoRowsOverlay,
					}}
					slotProps={{
						noRowsOverlay: {
							title: "No Current Trade",
							className: "!text-montserrat",
						},
					}}
				/>
				<Divider className="!my-8" />
				<Typography className={"!font-montserrat !text-[20px] !text-default !font-medium"}>Trade Edit</Typography>
				<Box className="mt-[32px]">
					<Typography className="!text-lg !font-montserrat !font-medium !text-default">{actionType.title}</Typography>
				</Box>
				<TransferRequestAction
					table="editTransferRequest"
					defaultSearchTransferRequest={defaultSearchTransferRequest}
					disableLocationOptionsAndValidation
					locationId={locationId}
					commissionCycleId={commissionCycleId}
					payPeriodId={payPeriodId}
					transferType={actionType.transferType as TTransferType}
					iconType={"Asset Swap"}
					setTransferType={() => {}}
					wrapperClasses="pt-[16px]"
					locationsOptions={{ mappedLocations: mappedLocationsArray, isLoadingLocations, franchisesByLocations }}
					onCreate={handleOnEdit}
					isCreatingTransferRequest={false}
					isCurrentPayPeriod={isCurrentPayPeriod}
				/>
			</DialogContent>
		</Dialog>
	);
};

export default EditPendingRequestDialog;
